import React from 'react';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import 'src/utils/yup';
import Label from 'shared/components/label';
import FormikTextInput from 'src/components/inputs/formik-text-input';
import FormikTelInput from 'src/components/inputs/formik-tel-input';
import FormikAddressInput from 'src/components/inputs/formik-address-input';
import Button from 'src/components/button';
import {AdminAccessOrganization} from 'src/services/AdminAccessOrganizationService';
import errors from 'src/static/errors.json';
import styles from './styles.module.scss';
import {errorMessage} from 'shared/actions/notifications';
import {useDispatch} from 'react-redux';

interface Props {
  organization?: AdminAccessOrganization;
  onSubmit: (values: AdminAccessOrganization) => Promise<void>;
  onCancel?: () => void;
}

interface FormValues {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  url?: string;
  slug?: string;
  landing_page_values?: {
    landing_text_1?: string;
    landing_text_2?: string;
    button_text?: string;
  };
}

const ACCESS_ORGANIZATION_VALIDATION_SCHEMA = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  address: Yup.string().required(errors.validation.address.addressLine1.required),
  city: Yup.string().required(errors.validation.address.city.required),
  state: Yup.string().required(errors.validation.address.subdivision.required),
  zip: Yup.string().required(errors.validation.address.postalCode.required),
  phone: Yup.string().phoneNumber(errors.validation.phone.invalid),
  url: Yup.string().url('Must be a valid URL').nullable(),
  slug: Yup.string(),
  landing_page_values: Yup.object().shape({
    landing_text_1: Yup.string(),
    landing_text_2: Yup.string(),
    button_text: Yup.string(),
  }),
});

const AccessOrganizationForm = ({organization, onSubmit, onCancel}: Props) => {
  const initialValues: FormValues = organization
    ? {
        ...organization,
        address: organization.address || '',
        state: organization.state || '',
        zip: organization.zip || '',
        phone: organization.phone || '',
      }
    : {
        name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        url: '',
        slug: '',
        landing_page_values: {
          landing_text_1: '',
          landing_text_2: '',
          button_text: '',
        },
      };

  const dispatch = useDispatch();

  const handleSubmit = async (values: FormValues, {resetForm}: {resetForm: () => void}) => {
    try {
      await onSubmit(values);
      resetForm();
    } catch {
      dispatch(errorMessage('Failed to submit organization'));
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={ACCESS_ORGANIZATION_VALIDATION_SCHEMA}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnBlur
      validateOnChange
    >
      {(formik) => (
        <Form className="access-organization-form">
          <div className="form-group">
            <Label required>
              Name
              <FormikTextInput name="name" placeholder="Name" required />
            </Label>
          </div>

          <FormikAddressInput
            formikFieldNames={{
              addressLine1: 'address',
              addressLine2: 'addressLine2',
              city: 'city',
              subdivision: 'state',
              postalCode: 'zip',
              country: 'country',
            }}
            includeFields={{
              addressLine1: true,
              addressLine2: false,
              city: true,
              subdivision: true,
              postalCode: true,
              country: false,
            }}
            required
          />

          <div className="form-group">
            <Label>
              Phone Number
              <FormikTelInput name="phone" placeholder="Phone Number" />
            </Label>
          </div>

          <div className="form-group">
            <Label>
              URL
              <FormikTextInput name="url" placeholder="URL" type="url" />
            </Label>
          </div>

          <div className="form-group">
            <Label>
              Join Page Slug
              <FormikTextInput name="slug" placeholder="Slug" />
            </Label>
          </div>

          <div className="form-group">
            <Label>
              Join Page Heading
              <FormikTextInput name="landing_page_values.landing_text_1" placeholder="Heading" />
            </Label>
          </div>

          <div className="form-group">
            <Label>
              Join Page Subheading
              <FormikTextInput name="landing_page_values.landing_text_2" placeholder="Subheading" />
            </Label>
          </div>

          <div className="form-group">
            <Label>
              Join Page Sign Up Button Text
              <FormikTextInput name="landing_page_values.button_text" placeholder="Button Text" />
            </Label>
          </div>

          <div className={styles.formActions}>
            <Button
              type="submit"
              disabled={formik.isSubmitting || (!formik.isValid && formik.submitCount > 0)}
            >
              {formik.isSubmitting ? 'Saving...' : 'Save'}
            </Button>
            {onCancel && (
              <Button type="button" onClick={onCancel} outline>
                Cancel
              </Button>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default AccessOrganizationForm;
